import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createLogto, type LogtoConfig, UserScope, useLogto } from '@logto/vue'
import App from './App.vue'
import router from './router'
import i18n from '@/i18n'
import './assets/main.css'
import './assets/tailwind.css'
import { envConfig } from '@/shared/config/env'
import { setLogtoBridgeBindings } from '@/shared/auth/logtoBridge'
import { LOGTO_SCOPES } from '@/manage/constants/access'
import { printConsoleBanner } from '@/utils/consoleBanner'

printConsoleBanner()

// logto 配置
const config: LogtoConfig = {
  endpoint: envConfig.logtoEndpoint,
  appId: envConfig.logtoAppId,
  resources: [envConfig.logtoResourceApi],
  scopes: [
    LOGTO_SCOPES.adminRead,
    LOGTO_SCOPES.adminWrite,
    LOGTO_SCOPES.contactFetchPage,
    LOGTO_SCOPES.contactFetchTag,
    LOGTO_SCOPES.reportExport,
    LOGTO_SCOPES.emailManage,
    LOGTO_SCOPES.emailSend,
    LOGTO_SCOPES.emailInbox,
    LOGTO_SCOPES.emailOutreach,
    LOGTO_SCOPES.inmail,
    LOGTO_SCOPES.agent,
    LOGTO_SCOPES.whatsapp,
    UserScope.Email,
    UserScope.Phone,
    UserScope.CustomData,
    UserScope.Identities,
    UserScope.Organizations,
    UserScope.Profile,
  ],
}

const app = createApp(App)
app.use(createLogto, config)
app.use(createPinia())

app.runWithContext(() => {
  const { getAccessToken, fetchUserInfo, isAuthenticated, error } = useLogto()
  setLogtoBridgeBindings({
    getAccessTokenResult: async (resource) => {
      const previousError = error.value
      const token = (await getAccessToken(resource)) ?? null
      const nextError = error.value
      return {
        token,
        error: token || nextError === previousError ? undefined : nextError,
      }
    },
    fetchUserInfoResult: async () => {
      const previousError = error.value
      const user = (await fetchUserInfo()) ?? null
      const nextError = error.value
      return {
        user,
        error: user || nextError === previousError ? undefined : nextError,
      }
    },
    fetchUserInfoLatestResult: async () => {
      const token = (await getAccessToken()) ?? null
      if (!token) return { user: null, error: undefined }
      try {
        const response = await fetch(`${envConfig.logtoEndpoint}/oidc/me?latest=true`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!response.ok) {
          return {
            user: null,
            error: new Error(`Failed to fetch latest user info: ${response.status}`),
          }
        }
        const user = (await response.json()) ?? null
        return { user, error: undefined }
      } catch (err) {
        return { user: null, error: err instanceof Error ? err : new Error(String(err)) }
      }
    },
    isAuthenticated: () => Boolean(isAuthenticated.value),
  })
})

app.use(router)
app.use(i18n)
app.mount('#app')
